import React from 'react';
import { Link } from 'react-router-dom';
import { RESPONSABLE, ULTIMA_ACTUALIZACION } from '../data/legal';

/**
 * Términos y Condiciones.
 *
 * Describe el servicio como funciona hoy, no como se lo imagina un modelo de
 * contrato genérico. Lo que más importa que esté bien dicho:
 *
 * - La seña la cobra el negocio en SU cuenta de Mercado Pago, conectada por
 *   OAuth. Aturno no recibe, no retiene y no puede devolver esa plata. Si el
 *   texto diera a entender lo contrario, cada reclamo por una devolución
 *   llegaría a la puerta equivocada.
 * - El turno con seña aparta el horario por unos minutos y se libera solo si no
 *   se paga. Es comportamiento real del sistema y la gente tiene que saberlo
 *   antes de que le pase.
 * - La suscripción se renueva sola hasta que se cancele, que es lo que hace el
 *   preapproval de Mercado Pago.
 *
 * Los datos del titular salen de data/legal.js, compartidos con la política de
 * privacidad, para que no se desincronicen.
 */

const TermsOfService = () => (
  <article className="legal-documento">
    <h1 className="legal-titulo">Términos y Condiciones</h1>
    <p className="legal-actualizado">Última actualización: {ULTIMA_ACTUALIZACION}</p>

    <section className="legal-resumen">
      <h2>En resumen</h2>
      <ul>
        <li>Aturno es la herramienta con la que un negocio maneja sus turnos; el servicio que se reserva lo presta el negocio, no nosotros.</li>
        <li>Las señas se pagan a la cuenta de Mercado Pago del negocio. Aturno no recibe ni retiene ese dinero.</li>
        <li>La suscripción se renueva sola cada mes hasta que la cancelás, y podés cancelarla cuando quieras.</li>
        <li>Un turno con seña aparta el horario unos minutos; si no se paga, vuelve a estar disponible.</li>
      </ul>
    </section>

    <section className="legal-seccion">
      <h2>1. Qué es Aturno y quién lo opera</h2>
      <p>
        Aturno es una plataforma que le permite a un negocio publicar sus servicios y horarios y
        recibir reservas de turnos por internet. La opera <strong>{RESPONSABLE.nombre}</strong>,
        con domicilio en {RESPONSABLE.domicilio}, en{' '}
        <a href={RESPONSABLE.sitio}>{RESPONSABLE.sitio}</a>.
      </p>
      <p>
        Al crear una cuenta o al reservar un turno aceptás estos términos. Si no estás de acuerdo,
        no uses el servicio.
      </p>
    </section>

    <section className="legal-seccion">
      <h2>2. Aturno no presta el servicio que reservás</h2>
      <p>
        Esto es lo más importante de todo el documento. Cuando reservás un turno, el acuerdo es
        entre vos y el negocio: él fija los precios, la duración, quién te atiende y su política de
        cancelación, y él es el responsable de atenderte. <strong>Aturno provee el software</strong>{' '}
        con el que ese negocio organiza su agenda, y nada más.
      </p>
      <p>
        Por eso, los reclamos por la calidad, el cumplimiento, la demora o la cancelación de un
        turno se resuelven con el negocio. Aturno no es parte de esa relación ni puede responder
        por ella.
      </p>
    </section>

    <section className="legal-seccion">
      <h2>3. La cuenta del negocio</h2>
      <ul>
        <li>Para abrir una cuenta hay que ser mayor de edad y dar datos reales.</li>
        <li>Sos responsable de tu contraseña y de lo que se haga desde tu cuenta. Avisanos si creés que alguien más entró.</li>
        <li>Sos responsable del contenido que publicás: nombre, descripción, imágenes, precios y datos de contacto.</li>
        <li>Sos responsable de cumplir con las obligaciones propias de tu actividad: habilitaciones, facturación, impuestos y las normas de defensa del consumidor frente a tus clientes.</li>
      </ul>
      <p>
        Podemos suspender o cerrar una cuenta que use el servicio para algo ilegal, que perjudique
        a otros usuarios o que intente vulnerar el funcionamiento de la plataforma.
      </p>
    </section>

    <section className="legal-seccion">
      <h2>4. Planes, precios y facturación</h2>
      <p>
        Aturno se ofrece en planes con distintos límites de servicios, personal, lugares y turnos
        por mes. El precio vigente es el que se muestra al momento de contratar.
      </p>
      <ul>
        <li>Hay un <strong>período de prueba gratuito</strong>. Cuando termina, para seguir usando la plataforma hay que contratar un plan.</li>
        <li>El cobro se hace a través de Mercado Pago mediante una <strong>suscripción que se renueva automáticamente cada mes</strong> hasta que la canceles.</li>
        <li>Podés cancelar cuando quieras desde tu panel. La cancelación evita las renovaciones siguientes; el período ya pagado sigue vigente hasta su fin.</li>
        <li>Si cambia el precio de tu plan, te lo avisamos antes de que se aplique, y podés cancelar si no estás de acuerdo.</li>
        <li>Si un cobro falla, podemos limitar el acceso a las funciones del plan hasta regularizarlo.</li>
      </ul>
      <div className="legal-aviso">
        <p>
          <strong>Derecho de revocación.</strong> Si contratás a distancia, podés arrepentirte
          dentro de los diez (10) días corridos de contratado el servicio, sin costo ni necesidad
          de justificar el motivo, conforme al artículo 34 de la Ley N° 24.240. Para ejercerlo
          escribinos a <a href={`mailto:${RESPONSABLE.email}`}>{RESPONSABLE.email}</a>.
        </p>
      </div>
    </section>

    <section className="legal-seccion">
      <h2>5. Señas y pagos entre el negocio y sus clientes</h2>
      <p>
        Un negocio puede pedir una seña para confirmar un turno. Es importante entender por dónde
        pasa esa plata:
      </p>
      <ul>
        <li>
          El negocio conecta <strong>su propia cuenta de Mercado Pago</strong> y el pago va
          directamente ahí. <strong>Aturno no recibe, no administra y no retiene ese dinero</strong>,
          y por lo tanto no puede devolverlo.
        </li>
        <li>
          Las devoluciones, la política de cancelación y el destino de la seña si el turno no se
          cumple los define el negocio, y se reclaman al negocio.
        </li>
        <li>
          También puede pedirse la seña por transferencia o en efectivo. En esos casos Aturno sólo
          muestra los datos que cargó el negocio y registra que el pago quedó pendiente de que él
          lo confirme.
        </li>
      </ul>

      <h3>El horario apartado mientras pagás</h3>
      <p>
        Cuando elegís un turno que pide seña, el horario queda <strong>apartado unos minutos</strong>{' '}
        para que puedas pagar sin que otra persona lo tome. Si en ese lapso el pago no se completa,
        la reserva se deja sin efecto y el horario vuelve a estar disponible para cualquiera. El
        link de pago vence junto con ese plazo.
      </p>
    </section>

    <section className="legal-seccion">
      <h2>6. Integraciones y avisos</h2>
      <ul>
        <li><strong>Google Calendar:</strong> si el negocio lo conecta, los turnos se copian a su agenda. Puede desconectarlo cuando quiera.</li>
        <li><strong>Correo:</strong> mandamos la confirmación y el recordatorio del turno a la dirección que se haya dejado.</li>
        <li><strong>WhatsApp:</strong> el recordatorio por WhatsApp requiere que lo hayas pedido expresamente al reservar. Algunos planes incluyen además un asistente que responde por ese canal; su contenido se procesa con servicios de terceros, como se explica en la <Link to="/legal/privacy">Política de Privacidad</Link>.</li>
      </ul>
    </section>

    <section className="legal-seccion">
      <h2>7. Disponibilidad del servicio</h2>
      <p>
        Trabajamos para que Aturno esté disponible y funcione bien, pero no garantizamos que vaya a
        estar libre de interrupciones o errores. Puede haber cortes por mantenimiento, por fallas
        propias o por fallas de los servicios de los que dependemos —Mercado Pago, Google, los
        proveedores de WhatsApp o el hosting—, que están fuera de nuestro control.
      </p>
      <p>
        Podemos modificar, agregar o discontinuar funciones. Si un cambio afecta de forma
        significativa lo que contrataste, te lo avisamos con antelación razonable.
      </p>
    </section>

    <section className="legal-seccion">
      <h2>8. Responsabilidad</h2>
      <p>
        En la medida en que lo permita la ley, Aturno responde por los daños directos que le sean
        imputables, con el límite de lo que hayas pagado por el servicio en los últimos doce meses.
        No respondemos por la relación entre el negocio y sus clientes, por el dinero de las señas,
        ni por las fallas de servicios de terceros.
      </p>
      <p>
        Nada de esto limita los derechos que la Ley N° 24.240 de Defensa del Consumidor te
        reconozca de manera irrenunciable.
      </p>
    </section>

    <section className="legal-seccion">
      <h2>9. Tus datos</h2>
      <p>
        Cómo tratamos los datos personales está explicado en la{' '}
        <Link to="/legal/privacy">Política de Privacidad</Link>, que forma parte de estos términos.
      </p>
    </section>

    <section className="legal-seccion">
      <h2>10. Cambios en estos términos</h2>
      <p>
        Si los modificamos, actualizamos la fecha del encabezado y publicamos la versión nueva acá.
        Si el cambio es importante lo avisamos dentro de la aplicación, y si seguís usando el
        servicio después de eso se entiende que lo aceptaste.
      </p>
    </section>

    <section className="legal-seccion">
      <h2>11. Ley aplicable y jurisdicción</h2>
      <p>
        Estos términos se rigen por las leyes de la República Argentina. Ante cualquier conflicto
        intervienen los tribunales ordinarios de la Ciudad Autónoma de Buenos Aires, sin perjuicio
        del fuero que corresponda a las personas consumidoras.
      </p>
      <p>
        Consultas: <a href={`mailto:${RESPONSABLE.email}`}>{RESPONSABLE.email}</a>
      </p>
    </section>
  </article>
);

export default TermsOfService;
